<template>
  <div class="po-upsert">
    <KTCodePreview v-bind:title="pageTitle">
      <template v-slot:preview>
        <b-overlay :show="loading" rounded="sm">
          <b-form @submit.prevent="onSubmit">
            <!-- Thông tin chung -->
            <div class="po-section">
              <h6 class="po-section-title">Thông tin chung</h6>
              <b-row>
                <b-col cols="12" md="4">
                  <b-form-group>
                    <label>
                      Nhà phân phối <span class="text-danger">*</span>
                    </label>
                    <b-form-select
                      v-model="form.providerId"
                      :options="providerOptions"
                      size="sm"
                      :state="validateState('providerId')"
                    />
                    <b-form-invalid-feedback>
                      Vui lòng chọn nhà phân phối
                    </b-form-invalid-feedback>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                  <b-form-group>
                    <label>Ngày dự kiến giao</label>
                    <b-form-input
                      v-model="form.expectedDeliveryDate"
                      type="date"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                  <b-form-group>
                    <label>Hình thức thanh toán</label>
                    <b-form-select
                      v-model="form.paymentMethod"
                      :options="paymentMethodOptions"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="12" md="4">
                  <b-form-group>
                    <label>
                      Ngân hàng
                      <span
                        v-if="needGuarantee || needDisbursement"
                        class="text-danger"
                        >*</span
                      >
                    </label>
                    <b-form-input
                      v-model="form.bankName"
                      size="sm"
                      placeholder="Tên ngân hàng"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                  <b-form-group>
                    <label>
                      Số bảo lãnh thanh toán
                      <span v-if="needGuarantee" class="text-danger">*</span>
                    </label>
                    <b-form-input
                      v-model="form.guaranteeNumber"
                      size="sm"
                      placeholder="Số bảo lãnh"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                  <b-form-group>
                    <label>
                      Số tiền
                      {{ needDisbursement ? '(phong tỏa)' : 'thanh toán' }}
                      <span v-if="needDisbursement" class="text-danger">*</span>
                    </label>
                    <b-form-input
                      v-model="form.paymentAmount"
                      type="text"
                      v-mask="mask"
                      size="sm"
                      placeholder="0"
                      class="text-right"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  v-if="needGuarantee || needDisbursement"
                  cols="12"
                  md="4"
                >
                  <b-form-group>
                    <label>
                      Ngày đáo hạn
                      {{ needDisbursement ? 'phong tỏa' : 'bảo lãnh' }}
                      <span class="text-danger">*</span>
                    </label>
                    <b-form-input
                      v-model="form.guaranteeDueDate"
                      type="date"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </div>

            <!-- Danh sách xe -->
            <div class="po-section">
              <div
                class="d-flex justify-content-between align-items-center mb-2"
              >
                <h6 class="po-section-title mb-0">
                  Danh sách xe <span class="text-danger">*</span>
                </h6>
                <b-button size="sm" variant="outline-primary" @click="addItem">
                  <i class="flaticon2-add-1"></i> Thêm dòng
                </b-button>
              </div>

              <table class="table table-bordered po-item-table">
                <thead>
                  <tr>
                    <th style="width: 24%">Model / Màu xe</th>
                    <th style="width: 16%">Kho</th>
                    <th style="width: 16%">Người duyệt nhận</th>
                    <th class="text-center" style="width: 9%">Số lượng</th>
                    <th class="text-right" style="width: 15%">Giá nhập</th>
                    <th class="text-right" style="width: 15%">Thành tiền</th>
                    <th style="width: 5%"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(it, idx) in form.items" :key="idx">
                    <td>
                      <multiselect
                        v-model="it.productSelected"
                        :options="productOptions"
                        :loading="productLoading"
                        :internal-search="false"
                        :clear-on-select="true"
                        :close-on-select="true"
                        :show-labels="false"
                        :show-no-results="true"
                        track-by="id"
                        label="label"
                        placeholder="Chọn / tìm model xe"
                        open-direction="bottom"
                        @search-change="onProductSearch"
                        @select="(opt) => onProductSelected(it, opt)"
                      >
                        <template slot="singleLabel" slot-scope="{ option }">
                          <div class="po-ms-single">
                            <span class="po-ms-name">{{ option.name }}</span>
                            <span v-if="option.code" class="po-ms-code">{{
                              option.code
                            }}</span>
                          </div>
                        </template>
                        <template slot="option" slot-scope="{ option }">
                          <div class="po-ms-option">
                            <span class="po-ms-name">{{ option.name }}</span>
                            <span v-if="option.code" class="po-ms-code">{{
                              option.code
                            }}</span>
                          </div>
                        </template>
                        <span slot="noResult">Không có kết quả</span>
                        <span slot="noOptions">Nhập để tìm model xe</span>
                      </multiselect>
                    </td>
                    <td>
                      <b-form-select
                        v-model="it.storeId"
                        :options="storeOptions"
                        size="sm"
                        @change="onItemStoreChange(it)"
                      />
                    </td>
                    <td>
                      <multiselect
                        v-model="it.receiverSelected"
                        :options="it.receiverOptions"
                        :disabled="!it.storeId || it.receiverLoading"
                        :loading="it.receiverLoading"
                        :internal-search="true"
                        :show-labels="false"
                        :close-on-select="true"
                        track-by="value"
                        label="search"
                        placeholder="Tìm tên / mã NV"
                        open-direction="bottom"
                        @select="(opt) => onReceiverSelected(it, opt)"
                        @remove="() => onReceiverRemoved(it)"
                      >
                        <template slot="singleLabel" slot-scope="{ option }">
                          <div class="po-ms-single">
                            <span class="po-ms-name">{{ option.name }}</span>
                            <span v-if="option.code" class="po-ms-code">{{
                              option.code
                            }}</span>
                          </div>
                        </template>
                        <template slot="option" slot-scope="{ option }">
                          <div class="po-ms-option">
                            <span class="po-ms-name">{{ option.name }}</span>
                            <span v-if="option.code" class="po-ms-code">{{
                              option.code
                            }}</span>
                          </div>
                        </template>
                        <span slot="noResult">Không có kết quả</span>
                        <span slot="noOptions">Chọn kho trước</span>
                      </multiselect>
                    </td>
                    <td>
                      <b-form-input
                        v-model.number="it.quantityOrdered"
                        type="number"
                        min="1"
                        size="sm"
                        class="text-center"
                      />
                    </td>
                    <td>
                      <b-form-input
                        v-model="it.importPrice"
                        type="text"
                        v-mask="mask"
                        size="sm"
                        class="text-right"
                        placeholder="0"
                      />
                    </td>
                    <td class="text-right align-middle">
                      <b>{{ formatPrice(lineTotal(it)) }}</b>
                    </td>
                    <td class="text-center align-middle">
                      <b-button
                        size="sm"
                        variant="link"
                        class="text-danger p-0"
                        @click="removeItem(idx)"
                      >
                        <i class="fas fa-trash"></i>
                      </b-button>
                    </td>
                  </tr>
                  <tr v-if="!form.items.length">
                    <td colspan="7" class="text-center text-muted py-3">
                      Chưa có dòng nào. Bấm "Thêm dòng" để thêm xe.
                    </td>
                  </tr>
                </tbody>
                <tfoot v-if="form.items.length">
                  <tr>
                    <td colspan="5" class="text-right font-weight-bold">
                      Tổng tiền
                    </td>
                    <td class="text-right">
                      <b class="po-total">{{ formatPrice(totalAmount) }}</b>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
              <b-form-invalid-feedback :state="itemsValid">
                Vui lòng thêm ít nhất 1 dòng xe hợp lệ (chọn model, kho, người
                duyệt, SL ≥ 1, giá ≥ 0).
              </b-form-invalid-feedback>
            </div>

            <b-alert v-if="errorMessage" variant="danger" show>
              {{ errorMessage }}
            </b-alert>

            <div class="d-flex justify-content-end mt-3">
              <b-button
                class="mr-2"
                size="sm"
                variant="secondary"
                @click="onCancel"
              >
                Hủy
              </b-button>
              <b-button
                type="submit"
                size="sm"
                variant="primary"
                :disabled="submitting"
              >
                <b-spinner v-if="submitting" small class="mr-1"></b-spinner>
                {{ isEdit ? 'Cập nhật' : 'Tạo Đơn đặt xe' }}
              </b-button>
            </div>
          </b-form>
        </b-overlay>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import { get } from 'lodash';
import moment from 'moment';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';

import KTCodePreview from '@/view/content/CodePreview.vue';
import ApiService from '@/core/services/api.service';
import PurchaseOrderService from '@/core/services/purchaseOrder.service';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import debounce from 'debounce';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import {
  PO_PAYMENT_METHOD,
  PO_PAYMENT_METHOD_OPTIONS,
} from '@/core/constants/purchaseOrder';
import { PRODUCT_TYPE_ENUM } from '@/utils/enum';
import {
  makeToastSuccess,
  makeToastFaile,
  convertPrice,
  unMaskMoney,
} from '@/utils/common';

// Mask tiền VND — dấu chấm phân cách nghìn (khớp định dạng hiển thị convertPrice
// locale vn), số nguyên (giá xe không có phần lẻ). unMaskMoney strip dấu chấm khi submit.
const VND_MONEY_MASK = createNumberMask({
  prefix: '',
  thousandsSeparatorSymbol: '.',
  includeThousandsSeparator: true,
  allowDecimal: false,
  allowNegative: false,
});

export default {
  name: 'PurchaseOrderUpsert',
  mixins: [validationMixin],
  components: { KTCodePreview, Multiselect },
  props: {
    id: { type: String, default: null },
  },
  data() {
    return {
      loading: false,
      submitting: false,
      errorMessage: '',
      // Mask tiền (text-mask) — value lưu dạng chuỗi có dấu phân cách nghìn,
      // unmask về số khi tính toán/submit.
      mask: VND_MONEY_MASK,
      // Options + loading dùng chung cho multiselect model (1 dropdown mở tại 1
      // thời điểm). Mỗi dòng item giữ object đã chọn ở `it.productSelected`.
      productOptions: [],
      productLoading: false,
      providerOptions: [{ value: null, text: '-- Chọn nhà phân phối --' }],
      // Kho dùng chung mọi dòng (kho user quản lý). Người duyệt nạp theo từng dòng.
      storeOptions: [{ value: null, text: '-- Chọn kho --' }],
      paymentMethodOptions: [
        { value: null, text: '-- Chọn hình thức --' },
        ...PO_PAYMENT_METHOD_OPTIONS,
      ],
      form: {
        providerId: null,
        expectedDeliveryDate: null,
        paymentMethod: null,
        bankName: '',
        guaranteeNumber: '',
        guaranteeDueDate: null,
        paymentAmount: '',
        items: [],
      },
    };
  },
  validations: {
    form: {
      providerId: { required },
    },
  },
  computed: {
    isEdit() {
      return !!this.id;
    },
    pageTitle() {
      return this.isEdit ? 'Sửa Đơn đặt xe' : 'Tạo Đơn đặt xe';
    },
    totalAmount() {
      return this.form.items.reduce((sum, it) => sum + this.lineTotal(it), 0);
    },
    // Bảo lãnh thanh toán → cần Ngân hàng + Số bảo lãnh.
    needGuarantee() {
      return this.form.paymentMethod === PO_PAYMENT_METHOD.FINANCIAL_GUARANTEE;
    },
    // Giải ngân phong tỏa → cần Ngân hàng + Số tiền (> 0).
    needDisbursement() {
      return (
        this.form.paymentMethod === PO_PAYMENT_METHOD.UNBLOCKING_DISBURSEMENT
      );
    },
    itemsValid() {
      if (!this.form.items.length) return false;
      return this.form.items.every(
        (it) =>
          it.productId &&
          it.storeId &&
          it.receiverId &&
          Number(it.quantityOrdered) >= 1 &&
          // importPrice là chuỗi masked → unmask trước khi so sánh số.
          Number(unMaskMoney(it.importPrice)) >= 0,
      );
    },
  },
  created() {
    this.fetchProviders();
    this.fetchStores();
    if (this.isEdit) {
      this.loadDetail();
    } else {
      this.addItem();
    }
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Đơn đặt xe', route: '/vehicle-purchase-orders' },
      { title: this.isEdit ? 'Sửa' : 'Tạo mới' },
    ]);
  },
  methods: {
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },
    lineTotal(it) {
      // importPrice là chuỗi masked ("1,000,000") → unmask về số trước khi nhân.
      return (
        (Number(it.quantityOrdered) || 0) *
        (Number(unMaskMoney(it.importPrice)) || 0)
      );
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return '-';
      return convertPrice(Number(value)) + ' đ';
    },
    addItem() {
      this.form.items.push({
        productId: null,
        // productName: tên model (giữ để hiển thị lại khi edit). Không gửi BE.
        productName: '',
        // productSelected: object option đang chọn của multiselect ({id,label,
        // name,code,importPrice}). Không gửi BE.
        productSelected: null,
        // Kho đích + người duyệt nhận (chọn lúc tạo đơn). receiverOptions nạp
        // theo storeId của dòng.
        storeId: null,
        receiverId: null,
        // receiverSelected: object option đang chọn của multiselect
        // ({value,text,name,code,search}). receiverId mới là cái gửi BE.
        receiverSelected: null,
        receiverOptions: [],
        receiverLoading: false,
        quantityOrdered: 1,
        // Chuỗi masked rỗng — user nhập, mask format dấu phân cách.
        importPrice: '',
      });
    },
    removeItem(idx) {
      this.form.items.splice(idx, 1);
    },
    // Map 1 product (BE) → option cho multiselect.
    toProductOption(p) {
      const name = p.name || '';
      const code = p.code || '';
      return {
        id: p.id,
        name,
        code,
        importPrice: p.importPrice,
        label: code ? `${code} - ${name}` : name || p.id,
      };
    },
    // @search-change của multiselect → debounce gọi API products (catalog đầy đủ).
    onProductSearch(query) {
      const text = (query || '').trim();
      if (!text) {
        this.productOptions = [];
        this.productLoading = false;
        return;
      }
      this.productLoading = true;
      this.debouncedProductSearch(text);
    },
    debouncedProductSearch: debounce(function (text) {
      this.searchProductsApi(text);
    }, 400),
    async searchProductsApi(text) {
      try {
        const { data } = await ApiService.query('products', {
          params: {
            page: 1,
            limit: 50,
            search: text,
            productType: PRODUCT_TYPE_ENUM.CHASSIS_ENGINE,
          },
        });
        const rows = get(data, 'data.data') || get(data, 'data') || [];
        this.productOptions = Array.isArray(rows)
          ? rows.map((p) => this.toProductOption(p))
          : [];
      } catch (err) {
        console.error('searchProductsApi error', err);
        this.productOptions = [];
      } finally {
        this.productLoading = false;
      }
    },
    // @select của multiselect → set productId + productName; prefill importPrice
    // nếu đang trống (giữ logic prefill cũ).
    onProductSelected(it, option) {
      if (!option) return;
      this.$set(it, 'productId', option.id);
      this.$set(it, 'productName', option.name || option.label || '');
      this.$set(it, 'productSelected', option);
      if (
        Number(unMaskMoney(it.importPrice)) === 0 &&
        Number(option.importPrice) > 0
      ) {
        this.$set(it, 'importPrice', convertPrice(Number(option.importPrice)));
      }
    },
    async fetchProviders() {
      try {
        const { data } = await ApiService.get('providers/getAll');
        const list = get(data, 'data.data') || get(data, 'data') || [];
        if (Array.isArray(list)) {
          this.providerOptions = [
            { value: null, text: '-- Chọn nhà phân phối --' },
            ...list.map((p) => ({
              value: p.id,
              text: p.name || p.shortName || p.id,
            })),
          ];
        }
      } catch (err) {
        console.error('fetchProviders error', err);
      }
    },
    async fetchStores() {
      try {
        const { data } = await ApiService.get('stores/get-by-user');
        const stores = get(data, 'data.stores') || get(data, 'data') || [];
        if (Array.isArray(stores)) {
          this.storeOptions = [
            { value: null, text: '-- Chọn kho --' },
            ...stores.map((s) => ({
              value: s.id,
              text: s.name || s.shortName || s.id,
            })),
          ];
        }
      } catch (err) {
        console.error('fetchStores error', err);
      }
    },
    // Map 1 user (BE) → option người duyệt cho multiselect. `search` gộp tên + mã
    // NV để internal-search lọc được theo CẢ tên lẫn mã số nhân viên.
    toReceiverOption(u) {
      const name = u.fullName || u.userName || u.email || u.id;
      const code = u.code || u.userName || '';
      return {
        value: u.id,
        text: name,
        name,
        code,
        search: code ? `${name} ${code}` : name,
      };
    },
    // @select multiselect người duyệt → sync receiverId (cái gửi BE).
    onReceiverSelected(it, option) {
      if (!option) return;
      this.$set(it, 'receiverId', option.value);
      this.$set(it, 'receiverSelected', option);
    },
    // @remove (bỏ chọn) → clear receiverId.
    onReceiverRemoved(it) {
      this.$set(it, 'receiverId', null);
      this.$set(it, 'receiverSelected', null);
    },
    // Đổi kho 1 dòng → reset người duyệt + nạp lại nhân viên kho đó.
    onItemStoreChange(it) {
      this.$set(it, 'receiverId', null);
      this.$set(it, 'receiverSelected', null);
      this.$set(it, 'receiverOptions', []);
      if (it.storeId) this.fetchReceiversForItem(it);
    },
    // Nạp nhân viên thuộc kho của dòng → options người duyệt. preserveId: giữ
    // receiverId hiện tại (dùng khi load edit) thay vì reset.
    async fetchReceiversForItem(it, preserveId) {
      this.$set(it, 'receiverLoading', true);
      try {
        const res = await ApiService.query('users/get-by-store', {
          params: { storeId: it.storeId },
        });
        const users = get(res, 'data.data') || get(res, 'data') || [];
        const opts = Array.isArray(users)
          ? users.map((u) => this.toReceiverOption(u))
          : [];
        this.$set(it, 'receiverOptions', opts);
        if (preserveId) {
          this.$set(it, 'receiverId', preserveId);
          // Đồng bộ object hiển thị cho multiselect (ưu tiên option có mã NV đầy đủ).
          const matched = opts.find((o) => o.value === preserveId);
          if (matched) this.$set(it, 'receiverSelected', matched);
        }
      } catch (err) {
        console.error('fetchReceiversForItem error', err);
        this.$set(it, 'receiverOptions', []);
      } finally {
        this.$set(it, 'receiverLoading', false);
      }
    },
    async loadDetail() {
      this.loading = true;
      try {
        const res = await PurchaseOrderService.getById(this.id);
        const po = get(res, 'data.data') || get(res, 'data') || {};
        this.form = {
          providerId: po.providerId || get(po, 'provider.id') || null,
          expectedDeliveryDate: po.expectedDeliveryDate
            ? moment(po.expectedDeliveryDate).format('YYYY-MM-DD')
            : null,
          paymentMethod: po.paymentMethod || null,
          bankName: po.bankName || '',
          guaranteeNumber: po.guaranteeNumber || '',
          guaranteeDueDate: po.guaranteeDueDate
            ? moment(po.guaranteeDueDate).format('YYYY-MM-DD')
            : null,
          // Format lại dạng masked để hiển thị có dấu phân cách nghìn.
          paymentAmount: po.paymentAmount
            ? convertPrice(Number(po.paymentAmount))
            : '',
          items: (po.items || []).map((it) => {
            // BE findOne trả kèm relation `product` → build option để
            // multiselect hiển thị sẵn model đã chọn khi edit.
            const name = get(it, 'product.name') || it.productName || '';
            const code = get(it, 'product.code') || it.productCode || '';
            const productSelected = it.productId
              ? {
                  id: it.productId,
                  name,
                  code,
                  importPrice: get(it, 'product.importPrice'),
                  label: code ? `${code} - ${name}` : name || it.productId,
                }
              : null;
            // Seed option người duyệt = người hiện tại (BE trả receiverName) để
            // multiselect hiển thị sẵn; fetchReceiversForItem sẽ nạp đủ list +
            // sync lại receiverSelected (kèm mã NV) bên dưới.
            const receiverSelected = it.receiverId
              ? {
                  value: it.receiverId,
                  text: it.receiverName || it.receiverId,
                  name: it.receiverName || it.receiverId,
                  code: it.receiverCode || '',
                  search: it.receiverName || it.receiverId,
                }
              : null;
            const receiverOptions = receiverSelected ? [receiverSelected] : [];
            return {
              productId: it.productId,
              productName: name,
              productSelected,
              storeId: it.storeId || null,
              receiverId: it.receiverId || null,
              receiverSelected,
              receiverOptions,
              receiverLoading: false,
              quantityOrdered: Number(it.quantityOrdered) || 1,
              // Masked string để input hiển thị có format.
              importPrice: convertPrice(Number(it.importPrice) || 0),
            };
          }),
        };
        if (!this.form.items.length) this.addItem();
        // Nạp đủ danh sách người duyệt cho từng dòng đã có kho (giữ receiverId cũ).
        this.form.items.forEach((it) => {
          if (it.storeId) this.fetchReceiversForItem(it, it.receiverId);
        });
      } catch (err) {
        makeToastFaile(
          get(err, 'response.data.message') ||
            err.message ||
            'Không tải được chi tiết',
        );
      } finally {
        this.loading = false;
      }
    },
    buildPayload() {
      return {
        providerId: this.form.providerId,
        expectedDeliveryDate: this.form.expectedDeliveryDate || undefined,
        paymentMethod: this.form.paymentMethod || undefined,
        bankName: this.form.bankName || undefined,
        guaranteeNumber: this.form.guaranteeNumber || undefined,
        guaranteeDueDate: this.form.guaranteeDueDate || undefined,
        // Unmask chuỗi tiền về số khi gửi BE.
        paymentAmount:
          this.form.paymentAmount !== null &&
          this.form.paymentAmount !== undefined &&
          this.form.paymentAmount !== ''
            ? Number(unMaskMoney(this.form.paymentAmount))
            : undefined,
        items: this.form.items.map((it) => ({
          productId: it.productId,
          quantityOrdered: Number(it.quantityOrdered),
          importPrice: Number(unMaskMoney(it.importPrice)),
          storeId: it.storeId,
          receiverId: it.receiverId,
        })),
      };
    },
    async onSubmit() {
      this.errorMessage = '';
      this.$v.form.$touch();
      if (this.$v.form.$anyError || !this.itemsValid) {
        if (!this.itemsValid) {
          makeToastFaile('Vui lòng kiểm tra lại danh sách xe');
        }
        return;
      }
      // Validation theo hình thức thanh toán (khớp BE).
      if (this.needGuarantee) {
        if (!String(this.form.bankName || '').trim()) {
          makeToastFaile('Bảo lãnh thanh toán: cần nhập Ngân hàng');
          return;
        }
        if (!String(this.form.guaranteeNumber || '').trim()) {
          makeToastFaile('Bảo lãnh thanh toán: cần nhập Số bảo lãnh');
          return;
        }
        if (!this.form.guaranteeDueDate) {
          makeToastFaile('Bảo lãnh thanh toán: cần nhập Ngày đáo hạn');
          return;
        }
      }
      if (this.needDisbursement) {
        if (!String(this.form.bankName || '').trim()) {
          makeToastFaile('Giải ngân phong tỏa: cần nhập Ngân hàng');
          return;
        }
        if (!(Number(unMaskMoney(this.form.paymentAmount)) > 0)) {
          makeToastFaile('Giải ngân phong tỏa: cần nhập Số tiền phong tỏa > 0');
          return;
        }
        if (!this.form.guaranteeDueDate) {
          makeToastFaile('Giải ngân phong tỏa: cần nhập Ngày đáo hạn');
          return;
        }
      }
      // Chặn trùng (model + kho) — cùng model khác kho thì được tách dòng.
      const keys = this.form.items
        .filter((it) => it.productId && it.storeId)
        .map((it) => `${it.productId}::${it.storeId}`);
      if (keys.some((k, i) => keys.indexOf(k) !== i)) {
        makeToastFaile(
          'Trùng model trong cùng 1 kho — gộp số lượng (cùng model khác kho thì được tách dòng).',
        );
        return;
      }
      this.submitting = true;
      try {
        const payload = this.buildPayload();
        if (this.isEdit) {
          await PurchaseOrderService.update(this.id, payload);
          makeToastSuccess('Đã cập nhật đơn mua xe');
        } else {
          await PurchaseOrderService.create(payload);
          makeToastSuccess('Đã tạo đơn mua xe');
        }
        this.$router.push({ name: 'list-purchase-order' });
      } catch (err) {
        this.errorMessage =
          get(err, 'response.data.message') || err.message || 'Lưu thất bại';
      } finally {
        this.submitting = false;
      }
    },
    onCancel() {
      this.$router.push({ name: 'list-purchase-order' });
    },
  },
};
</script>

<style lang="scss" scoped>
$po-border: #e5eaee;
$po-total: #2e7d32;

.po-upsert {
  .po-section {
    background: #fff;
    border: 1px solid $po-border;
    border-radius: 6px;
    padding: 14px;
    margin-bottom: 14px;
  }
  .po-section-title {
    font-size: 13px;
    font-weight: 700;
    color: #181c32;
    margin-bottom: 12px;
  }
  .po-item-table {
    th {
      background-color: #f3f6f9;
      font-size: 12px;
      vertical-align: middle;
    }
    td {
      vertical-align: middle;
    }
  }
  .po-total {
    color: $po-total;
    font-size: 15px;
  }
  // Slot model trong multiselect: tên là chính, mã nhỏ/mono phụ.
  .po-ms-option,
  .po-ms-single {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
    .po-ms-name {
      font-size: 13px;
      font-weight: 500;
      color: #3f4254;
      // Tên dài cho xuống hàng (cả dropdown lẫn ô đã chọn) — tránh cắt mất chữ
      // gây nhầm model.
      white-space: normal;
      word-break: break-word;
      overflow-wrap: anywhere;
    }
    .po-ms-code {
      font-size: 11px;
      color: #b5b5c3;
      font-family: 'SF Mono', Menlo, Consolas, monospace;
      font-variant-numeric: tabular-nums;
      letter-spacing: 0.02em;
    }
  }
}
</style>

<!-- Override vue-multiselect theo Metronic — KHÔNG scoped để chạm class nội bộ,
     nhưng prefix .po-upsert để không ảnh hưởng multiselect khác toàn app. -->
<style lang="scss">
.po-upsert {
  .multiselect {
    min-height: 31px;
  }
  // Flex + align-items center → mọi nội dung (placeholder / single / input) căn
  // GIỮA theo chiều dọc của ô, không lệ thuộc padding-top thủ công (vốn lệch khi
  // single label cao 2 dòng tên + mã).
  .multiselect__tags {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    min-height: 31px;
    height: auto;
    padding: 2px 40px 2px 8px;
    border: 1px solid #e4e6ef;
    border-radius: 6px;
    font-size: 13px;
  }
  .multiselect__input,
  .multiselect__single {
    font-size: 13px;
    line-height: 1.3;
    margin-bottom: 0;
    padding: 0;
    background: transparent;
    white-space: normal;
  }
  // KHÔNG ép min-width/flex-grow cho input: vue-multiselect tự set width=0 khi đã
  // chọn giá trị (đóng) để ẩn input. Nếu ép rộng, native placeholder của input sẽ
  // đè lên nhãn đã chọn (che mất tên SP / tên người duyệt).
  .multiselect__input {
    flex: 0 1 auto;
    min-width: 0;
  }
  .multiselect__placeholder {
    font-size: 13px;
    color: #b5b5c3;
    padding: 0;
    margin-bottom: 0;
    line-height: 1.3;
  }
  .multiselect__select {
    height: 29px;
  }
  .multiselect__content-wrapper {
    background: #fff;
    border: 1px solid #e4e6ef;
    border-radius: 6px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
    z-index: 60;
  }
  .multiselect__option {
    min-height: auto;
    padding: 8px 12px;
    font-size: 13px;
  }
  // Bỏ nền xanh lá mặc định → hover xám nhạt, chữ tối.
  .multiselect__option--highlight {
    background: #f3f6f9;
    color: #3f4254;
  }
  .multiselect__option--highlight::after {
    display: none;
  }
  .multiselect__option--selected {
    background: #ebedf3;
    color: #3f4254;
    font-weight: 500;
  }
  .multiselect__option--selected.multiselect__option--highlight {
    background: #e1e3ea;
    color: #3f4254;
  }
}
</style>
